import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Lock, Unlock, Shield, Loader2, ArrowLeft, Plus, Trash2, Search, Upload,
  Calendar, AlertTriangle, ShieldCheck, Share2, KeyRound, Sparkles, Eye, EyeOff,
  X, FileText, Download, Copy, Check, Users, Fingerprint, Bell, Phone,
} from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

// ───────────────────────────────────────────────────────────────────────
// WebAuthn base64url utils. Server returns URL-safe base64 challenges/ids
// and expects the same encoding back. The browser API works in ArrayBuffers.
// ───────────────────────────────────────────────────────────────────────
function _b64urlToBuf(s) {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}
function _bufToB64url(buf) {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/=+$/, "").replace(/\+/g, "-").replace(/\//g, "_");
}
function _wa_prepareCreate(opts) {
  return {
    ...opts,
    challenge: _b64urlToBuf(opts.challenge),
    user: { ...opts.user, id: _b64urlToBuf(opts.user.id) },
    excludeCredentials: (opts.excludeCredentials || []).map(c => ({ ...c, id: _b64urlToBuf(c.id) })),
  };
}
function _wa_prepareGet(opts) {
  return {
    ...opts,
    challenge: _b64urlToBuf(opts.challenge),
    allowCredentials: (opts.allowCredentials || []).map(c => ({ ...c, id: _b64urlToBuf(c.id) })),
  };
}
function _wa_serializeCreate(cred) {
  return {
    id: cred.id, rawId: _bufToB64url(cred.rawId), type: cred.type,
    response: {
      attestationObject: _bufToB64url(cred.response.attestationObject),
      clientDataJSON:    _bufToB64url(cred.response.clientDataJSON),
    },
  };
}
function _wa_serializeGet(cred) {
  return {
    id: cred.id, rawId: _bufToB64url(cred.rawId), type: cred.type,
    response: {
      authenticatorData: _bufToB64url(cred.response.authenticatorData),
      clientDataJSON:    _bufToB64url(cred.response.clientDataJSON),
      signature:         _bufToB64url(cred.response.signature),
      userHandle:        cred.response.userHandle ? _bufToB64url(cred.response.userHandle) : null,
    },
  };
}
function _deviceLabel() {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "iPhone / iPad";
  if (/Android/i.test(ua)) return "Android device";
  if (/Mac/i.test(ua)) return "Mac";
  if (/Windows/i.test(ua)) return "Windows PC";
  return "This device";
}

// Human-friendly placeholders for the metadata fields the catalog exposes.
// Anything not listed here falls back to the field name with underscores → spaces.
const FIELD_PLACEHOLDERS = {
  // PAN
  pan_number:          "AAAAA9999A",
  name_on_card:        "As printed on card",
  father_name:         "Father's full name",
  date_of_birth:       "YYYY-MM-DD",
  // Aadhaar
  aadhaar_number:      "1234 5678 9012",
  vid:                 "16-digit Virtual ID (optional)",
  address:             "As on card",
  gender:              "M / F / X",
  // GST
  legal_business_name: "Registered legal name",
  trade_name:          "Doing-business-as name",
  gstin:               "22AAAAA0000A1Z5",
  pan:                 "AAAAA9999A",
  constitution:        "Proprietorship / LLP / Pvt Ltd",
  state:               "State of registration",
  registration_date:   "YYYY-MM-DD",
  // ATM / Debit Card
  bank_name:           "HDFC / SBI / ICICI …",
  card_holder_name:    "Name embossed on card",
  card_last4:          "Last 4 digits only",
  network:             "Visa / Mastercard / RuPay",
  account_masked:      "e.g. XXXX1234",
  expiry:              "MM/YY",
  pin_hint:            "Personal reminder — never the real PIN",
};

// Small helper: renders a metadata Input with a password-style toggle if the
// field is flagged as sensitive by the backend catalog. Falls back to a plain
// input otherwise — the API contract is unchanged.
function SensitiveField({ name, value, sensitive, placeholder, onChange }) {
  const [reveal, setReveal] = React.useState(false);
  const label = name.replace(/_/g, " ");
  return (
    <div className={name === "address" ? "col-span-2" : ""}>
      <label className="text-[10px] uppercase font-black text-white/60">{label}</label>
      <div className="relative mt-1">
        <Input value={value} onChange={e => onChange(e.target.value)}
               type={sensitive && !reveal ? "password" : "text"}
               placeholder={placeholder || ""}
               className="rounded-full bg-white/5 border-white/10 text-white text-sm pr-9"
               data-testid={`doc-md-${name}`}/>
        {sensitive && (
          <button type="button" onClick={() => setReveal(r => !r)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                  data-testid={`doc-md-${name}-reveal`} aria-label={reveal ? "Hide" : "Show"}>
            {reveal ? <EyeOff size={12}/> : <Eye size={12}/>}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * /locker — Digi Asset Locker. Secure vault for LIC, insurance, property,
 * degrees, passports, driving licences, warranties, IDs, medical records, wills.
 *
 * Features:
 * - PIN-gated view/download (4 or 6 digits)
 * - Expiry reminders (60/30/7 day buckets)
 * - Base64 file attachments (≤ 5 MB each)
 * - Family/Nominee share via signed links
 * - Gemini OCR to auto-fill fields from a photo
 * - Insurance claim tracker
 */
export default function DigiLocker() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [webauthn, setWebauthn] = useState({ enabled: false, credentials: [] });
  const [renewals, setRenewals] = useState({ total: 0, docs: [] });
  const [renewalModal, setRenewalModal] = useState(null);   // { doc, quotes }
  const [docs, setDocs] = useState([]);
  const [filterCat, setFilterCat] = useState(null);
  const [q, setQ] = useState("");
  const [pin, setPin] = useState("");                // session PIN
  const [unlockToken, setUnlockToken] = useState(""); // biometric unlock token
  const [pinUnlocked, setPinUnlocked] = useState(false);
  const [pinModal, setPinModal] = useState(null);    // { mode: 'unlock'|'set'|'clear' }
  const [editDoc, setEditDoc] = useState(null);
  const [viewDoc, setViewDoc] = useState(null);
  const [shareModal, setShareModal] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [backupOpen, setBackupOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const bioSupported = typeof window !== "undefined" && !!(window.PublicKeyCredential);
  useSeo({ title: "Digi Asset Locker · find2near",
           description: "Secure vault for LIC, insurance, property deeds, degrees, passports, driving licences, warranties and IDs — with expiry reminders, nominee share and PIN protection." });

  const loadAll = async () => {
    try {
      const [s, c, se, d, w, r] = await Promise.all([
        api.get("/locker/stats"),
        api.get("/locker/categories"),
        api.get("/locker/settings"),
        api.get("/locker/documents"),
        api.get("/locker/webauthn/status").catch(() => ({ data: { enabled: false, credentials: [] } })),
        api.get("/locker/renewals/upcoming", { params: { window_days: 30 } }).catch(() => ({ data: { total: 0, docs: [] } })),
      ]);
      setStats(s.data); setCategories(c.data); setSettings(se.data); setDocs(d.data); setWebauthn(w.data); setRenewals(r.data);
    } catch (e) {
      if (e?.response?.status === 401) return;
      toast.error("Failed to load locker");
    }
  };
  useEffect(() => { if (user) loadAll(); }, [user]);

  const filtered = useMemo(() => {
    return docs.filter(d => {
      if (filterCat && d.category !== filterCat) return false;
      if (q && !(d.title.toLowerCase().includes(q.toLowerCase()) ||
                 (d.tags || []).some(t => t.toLowerCase().includes(q.toLowerCase())))) return false;
      return true;
    });
  }, [docs, filterCat, q]);

  const groups = useMemo(() => {
    const g = {};
    categories.forEach(c => { g[c.group] = g[c.group] || []; g[c.group].push(c); });
    return g;
  }, [categories]);

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-950 text-white grid place-items-center p-6">
        <div className="rounded-3xl bg-white/5 border border-white/10 p-6 text-center max-w-sm">
          <Lock size={36} className="mx-auto text-amber-400 mb-2"/>
          <div className="text-lg font-black">Sign in to unlock</div>
          <div className="text-[12px] text-white/60 mt-1">Your locker is private — end-to-end tied to your account.</div>
        </div>
      </div>
    );
  }

  const openView = async (d) => {
    if (settings?.pin_enabled && !pinUnlocked && !unlockToken) {
      setPinModal({ mode: "unlock", after: () => openView(d) });
      return;
    }
    try {
      const params = {};
      if (pin) params.pin = pin;
      if (unlockToken) params.unlock = unlockToken;
      const r = await api.get(`/locker/documents/${d.doc_id}`, { params });
      setViewDoc(r.data);
    } catch (e) {
      if (e?.response?.status === 423 || e?.response?.status === 403) {
        setPinUnlocked(false); setPin(""); setUnlockToken("");
        setPinModal({ mode: "unlock", after: () => openView(d) });
      } else toast.error("Couldn't open document");
    }
  };

  // WebAuthn helpers -----------------------------------------------------
  const enrollBiometric = async () => {
    if (!bioSupported) { toast.error("Biometrics not supported on this device"); return; }
    try {
      const opts = await api.post("/locker/webauthn/register/begin");
      const publicKey = _wa_prepareCreate(opts.data);
      const cred = await navigator.credentials.create({ publicKey });
      const payload = _wa_serializeCreate(cred);
      const r = await api.post("/locker/webauthn/register/finish", {
        label: _deviceLabel(), credential: payload,
      });
      toast.success(`Face Unlock enabled on ${r.data.label}`);
      loadAll();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Enrollment cancelled";
      if (String(msg).includes("NotAllowed")) toast.error("Biometric prompt was dismissed");
      else toast.error(msg);
    }
  };

  const unlockBiometric = async () => {
    if (!bioSupported) { toast.error("Biometrics not supported"); return; }
    try {
      const opts = await api.post("/locker/webauthn/auth/begin");
      const publicKey = _wa_prepareGet(opts.data);
      const assertion = await navigator.credentials.get({ publicKey });
      const payload = _wa_serializeGet(assertion);
      const r = await api.post("/locker/webauthn/auth/finish", { credential: payload });
      setUnlockToken(r.data.unlock_token);
      setPinUnlocked(true);
      toast.success("Locker unlocked with biometrics");
      loadAll();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Biometric auth failed";
      if (String(msg).includes("NotAllowed")) toast.error("Biometric prompt was dismissed");
      else toast.error(msg);
    }
  };

  const removeBiometric = async (cid) => {
    if (!confirm("Remove this device from Face Unlock?")) return;
    try { await api.delete(`/locker/webauthn/credentials/${cid}`); toast.success("Removed"); loadAll(); }
    catch { toast.error("Remove failed"); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-black text-white" data-testid="locker-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-amber-600 to-rose-700 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/apps" className="text-[11px] text-white/70 hover:text-white inline-flex items-center gap-1"><ArrowLeft size={12}/> Apps</Link>
          <div className="text-[11px] uppercase tracking-widest font-black opacity-80 flex items-center gap-1 mt-2"><Shield size={12}/> Digi Asset Locker</div>
          <h1 className="heading text-3xl md:text-5xl font-black mt-1">Your documents. Safely stored. Always ready.</h1>
          <p className="text-sm text-white/90 mt-1">LIC · Term · Health · Vehicle Insurance · Property · Degrees · Passport · Licence · Warranty · IDs · Wills · Medical Records — with expiry alerts, PIN lock and nominee share.</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-3">
        {/* Stats + settings */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="locker-stats">
          <Stat label="Documents"  value={stats?.total_documents ?? "—"} icon={<FileText size={14} className="text-emerald-400"/>}/>
          <Stat label="Expiring 60d" value={stats?.expiring_soon ?? "—"} icon={<AlertTriangle size={14} className="text-amber-400"/>} accent={stats?.expiring_soon > 0 ? "text-amber-300" : ""}/>
          <Stat label="Active shares" value={stats?.active_shares ?? "—"} icon={<Users size={14} className="text-sky-400"/>}/>
          <Stat label="Open claims" value={stats?.open_claims ?? "—"} icon={<Sparkles size={14} className="text-rose-400"/>}/>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2" data-testid="locker-controls">
          <Button onClick={() => setEditDoc({ __new: true, category: categories[0]?.key, title: "", metadata: {}, tags: [], attachments: [] })}
                  className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[11px] h-9" data-testid="locker-add-btn">
            <Plus size={12} className="mr-1"/> Add document
          </Button>
          {settings && (settings.pin_enabled ? (
            <>
              {pinUnlocked ? (
                <Button onClick={() => { setPin(""); setUnlockToken(""); setPinUnlocked(false); toast.info("Locker re-locked"); }}
                        className="rounded-full bg-white/10 hover:bg-white/20 text-white font-black text-[11px] h-9" data-testid="locker-relock-btn">
                  <Lock size={12} className="mr-1"/> Re-lock
                </Button>
              ) : (
                <>
                  {webauthn.enabled && bioSupported && (
                    <Button onClick={unlockBiometric}
                            className="rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-black text-[11px] h-9" data-testid="locker-bio-unlock-btn">
                      <Fingerprint size={12} className="mr-1"/> Face / Touch unlock
                    </Button>
                  )}
                  <Button onClick={() => setPinModal({ mode: "unlock" })}
                          className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-black text-[11px] h-9" data-testid="locker-unlock-btn">
                    <Unlock size={12} className="mr-1"/> Unlock ({settings.pin_length}-digit)
                  </Button>
                </>
              )}
              {bioSupported && !webauthn.enabled && (
                <Button onClick={enrollBiometric}
                        className="rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-black text-[11px] h-9" data-testid="locker-bio-enroll-btn">
                  <Fingerprint size={12} className="mr-1"/> Enable Face Unlock
                </Button>
              )}
              {webauthn.enabled && (
                <div className="inline-flex items-center gap-1 rounded-full bg-white/5 border border-white/10 h-9 px-3 text-[11px] font-black text-fuchsia-300" data-testid="locker-bio-status">
                  <Fingerprint size={12}/> {webauthn.credentials.length} device{webauthn.credentials.length !== 1 ? "s" : ""} enrolled
                </div>
              )}
              <Button onClick={() => setPinModal({ mode: "clear" })}
                      className="rounded-full bg-white/10 hover:bg-white/20 text-white font-black text-[11px] h-9" data-testid="locker-pin-clear-btn">
                <KeyRound size={12} className="mr-1"/> Remove PIN
              </Button>
            </>
          ) : (
            <Button onClick={() => setPinModal({ mode: "set" })}
                    className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-black text-[11px] h-9" data-testid="locker-pin-set-btn">
              <KeyRound size={12} className="mr-1"/> Set Locker PIN
            </Button>
          ))}
          <Button onClick={() => setShareModal({ __picker: true })}
                  className="rounded-full bg-sky-500 hover:bg-sky-600 text-white font-black text-[11px] h-9" data-testid="locker-share-btn">
            <Share2 size={12} className="mr-1"/> Share with nominee
          </Button>
          <Button onClick={() => setAuditOpen(true)}
                  className="rounded-full bg-white/10 hover:bg-white/20 text-white font-black text-[11px] h-9" data-testid="locker-audit-btn">
            <Eye size={12} className="mr-1"/> Shared with
          </Button>
          <Button onClick={() => setBackupOpen(true)}
                  className="rounded-full bg-white/10 hover:bg-white/20 text-white font-black text-[11px] h-9" data-testid="locker-backup-btn">
            <Download size={12} className="mr-1"/> Cloud backup
          </Button>
        </div>

        {/* Search + filters */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"/>
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search title or tag"
                   className="pl-9 rounded-full bg-white/5 border-white/10 text-white placeholder:text-white/40"
                   data-testid="locker-search-input"/>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="locker-category-chips">
          <button onClick={() => setFilterCat(null)}
                  className={`rounded-full text-[11px] font-black px-3 py-1.5 ${filterCat === null ? "bg-white text-slate-900" : "bg-white/10 text-white"}`}
                  data-testid="locker-cat-all">All</button>
          {categories.map(c => (
            <button key={c.key} onClick={() => setFilterCat(c.key)}
                    className={`rounded-full text-[11px] font-black px-3 py-1.5 ${filterCat === c.key ? "bg-white text-slate-900" : "bg-white/10 text-white"}`}
                    data-testid={`locker-cat-${c.key}`}>
              {c.label}
            </button>
          ))}
        </div>

        {/* Auto-Insurance Renewal ribbon */}
        {renewals.total > 0 && (
          <div className="rounded-3xl bg-gradient-to-br from-amber-500/20 to-rose-500/20 border border-amber-400/30 p-3 space-y-2" data-testid="locker-renewals-ribbon">
            <div className="flex items-center gap-2">
              <Bell size={14} className="text-amber-300"/>
              <div className="text-[10px] uppercase font-black text-amber-300">Renewals coming up</div>
              <span className="rounded-full bg-amber-400 text-slate-900 text-[10px] font-black px-2 py-0.5">{renewals.total}</span>
            </div>
            <div className="space-y-1.5">
              {renewals.docs.slice(0, 3).map(r => (
                <div key={r.doc_id} className="rounded-2xl bg-black/30 border border-white/10 p-2 flex items-center gap-2" data-testid={`renewal-row-${r.doc_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-sm text-white truncate">{r.title}</div>
                    <div className="text-[10px] text-white/60">Expires in <span className={r.expiring_in_days <= 7 ? "text-rose-300 font-black" : "text-amber-300 font-black"}>{r.expiring_in_days} days</span>{r.current_provider ? ` · currently with ${r.current_provider}` : ""}</div>
                  </div>
                  <Button onClick={() => setRenewalModal(r)}
                          className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-black text-[10px] h-7 px-3" data-testid={`renewal-open-${r.doc_id}`}>
                    <Sparkles size={11} className="mr-1"/> See quotes
                  </Button>
                </div>
              ))}
              {renewals.docs.length > 3 && <div className="text-[10px] text-white/50">+ {renewals.docs.length - 3} more</div>}
            </div>
          </div>
        )}

        {/* Doc list */}
        {filtered.length === 0 ? (
          <div className="text-center text-sm text-white/50 py-10 rounded-3xl border border-dashed border-white/10" data-testid="locker-empty">
            No documents yet. Tap <span className="text-emerald-300 font-black">Add document</span> to save your first one.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-2" data-testid="locker-doc-list">
            {filtered.map(d => (
              <DocCard key={d.doc_id} d={d} onView={() => openView(d)} onEdit={() => setEditDoc(d)}
                       onShare={() => setShareModal({ __one: d.doc_id, __title: d.title })}/>
            ))}
          </div>
        )}
      </div>

      {pinModal && <PinModal mode={pinModal.mode} settings={settings}
                              onClose={() => setPinModal(null)}
                              onDone={async (pinValue) => {
                                if (pinModal.mode === "unlock") { setPin(pinValue); setPinUnlocked(true); }
                                await loadAll();
                                setPinModal(null);
                                pinModal.after?.();
                              }}/>}
      {editDoc  && <DocEditor doc={editDoc} categories={categories}
                              onClose={() => setEditDoc(null)}
                              onSaved={() => { setEditDoc(null); loadAll(); }}/>}
      {viewDoc  && <DocViewer d={viewDoc} onClose={() => setViewDoc(null)}
                              onDeleted={() => { setViewDoc(null); loadAll(); }}/>}
      {shareModal && <ShareBuilder ctx={shareModal} docs={docs} onClose={() => setShareModal(null)}
                                    onCreated={() => { setShareModal(null); loadAll(); }}/>}
      {auditOpen && <ShareAuditPanel onClose={() => setAuditOpen(false)}/>}
      {backupOpen && <CloudBackupDialog onClose={() => setBackupOpen(false)}/>}
      {renewalModal && <RenewalModal r={renewalModal} onClose={() => setRenewalModal(null)}
                                      onRequested={() => { setRenewalModal(null); loadAll(); }}/>}
    </div>
  );
}

function RenewalModal({ r, onClose, onRequested }) {
  const [busy, setBusy] = useState(null);
  const [phone, setPhone] = useState("");
  const [placed, setPlaced] = useState(null);

  const requestQuote = async (partner) => {
    setBusy(partner.partner_id);
    try {
      const res = await api.post("/locker/renewals/request", {
        document_id: r.doc_id,
        partner_id: partner.partner_id,
        contact_phone: phone || undefined,
      });
      setPlaced({ ...res.data, partner });
      toast.success(`Forwarded to ${partner.name} — expect a call-back`);
      onRequested?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Request failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 grid place-items-end sm:place-items-center p-2 sm:p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose} data-testid="renewal-modal">
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-slate-900 border border-white/10 p-5 w-full max-w-md space-y-3 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase font-black text-amber-300 flex items-center gap-1"><Sparkles size={11}/> 1-tap renewal</div>
            <h3 className="font-black text-white text-sm mt-0.5" data-testid="renewal-doc-title">{r.title}</h3>
            <div className="text-[11px] text-white/60">Expires in <span className="text-amber-300 font-black">{r.expiring_in_days} days</span></div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={14}/></button>
        </div>

        {placed ? (
          <div className="rounded-2xl bg-emerald-500/10 border border-emerald-400/30 p-3 space-y-2" data-testid="renewal-placed">
            <div className="text-[10px] uppercase font-black text-emerald-300">Request forwarded</div>
            <div className="text-sm font-black text-emerald-100">{placed.partner.name} will get in touch shortly.</div>
            <div className="text-[10px] text-emerald-200">Request ID: {placed.request_id}</div>
            {placed.call_url && (
              <a href={placed.call_url} target="_blank" rel="noreferrer"
                 className="block text-center rounded-full bg-emerald-500 text-slate-900 font-black text-[11px] py-2" data-testid="renewal-partner-link">
                Get instant quote on {placed.partner.name}
              </a>
            )}
            <Button onClick={onClose} className="rounded-full bg-white/10 text-white font-black text-[11px] w-full">Done</Button>
          </div>
        ) : (
          <>
            <div>
              <label className="text-[10px] uppercase font-black text-white/60">Phone for call-back (optional)</label>
              <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91 90000 00000"
                     className="rounded-full bg-white/5 border-white/10 text-white text-sm mt-1" data-testid="renewal-phone"/>
            </div>
            <div className="space-y-1.5" data-testid="renewal-quote-list">
              {(r.quotes || []).map(q => (
                <div key={q.partner_id} className="rounded-2xl bg-white/5 border border-white/10 p-3 flex items-center gap-2" data-testid={`renewal-quote-${q.partner_id}`}>
                  <div className="text-2xl w-8 text-center">{q.logo}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-sm text-white truncate">{q.name}</div>
                    <div className="text-[10px] text-white/60 truncate">{q.tagline}</div>
                    {q.estimated_premium != null && (
                      <div className="text-[11px] text-emerald-300 font-black mt-0.5">
                        Est. ₹{q.estimated_premium.toLocaleString("en-IN")}
                        {q.savings > 0 && <> · <span className="text-emerald-400">Save ₹{q.savings.toLocaleString("en-IN")}</span></>}
                      </div>
                    )}
                  </div>
                  <Button disabled={busy === q.partner_id} onClick={() => requestQuote(q)}
                          className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[10px] h-7 px-2" data-testid={`renewal-request-${q.partner_id}`}>
                    {busy === q.partner_id ? <Loader2 size={11} className="animate-spin"/> : <><Phone size={11} className="mr-1"/> Get quote</>}
                  </Button>
                </div>
              ))}
            </div>
            <div className="text-[10px] text-white/40">Estimates are indicative — the partner confirms the final quote on the call. Your document metadata is not sent — only the category + expiry.</div>
          </>
        )}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
function Stat({ label, value, icon, accent }) {
  return (
    <div className="bg-white/5 rounded-2xl border border-white/10 p-3">
      <div className="text-[10px] uppercase text-white/50 font-black flex items-center gap-1">{icon} {label}</div>
      <div className={`text-2xl font-black mt-1 ${accent || ""}`}>{value}</div>
    </div>
  );
}

function urgencyClass(days) {
  if (days == null) return "text-white/40";
  if (days <= 0)   return "text-rose-400";
  if (days <= 7)   return "text-rose-400";
  if (days <= 30)  return "text-amber-400";
  if (days <= 60)  return "text-yellow-300";
  return "text-emerald-400";
}

function DocCard({ d, onView, onEdit, onShare }) {
  const days = d.expiring_in_days;
  return (
    <div className="bg-white/5 rounded-2xl border border-white/10 p-3 flex flex-col gap-1" data-testid={`locker-doc-${d.doc_id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase text-emerald-300 font-black truncate">{d.group} · {d.category}</div>
          <div className="font-black text-sm text-white truncate" data-testid={`locker-doc-title-${d.doc_id}`}>{d.title}</div>
          {d.metadata && Object.keys(d.metadata).length > 0 && (
            <div className="text-[10px] text-white/60 truncate">
              {Object.entries(d.metadata).slice(0, 3).map(([k, v]) => `${k}: ${v}`).join(" · ")}
            </div>
          )}
        </div>
        {d.attachment_count > 0 && (
          <div className="rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-black px-2 py-0.5">{d.attachment_count} file{d.attachment_count > 1 ? "s" : ""}</div>
        )}
      </div>
      {d.expires_on && (
        <div className={`text-[10px] font-black inline-flex items-center gap-1 ${urgencyClass(days)}`} data-testid={`locker-doc-expiry-${d.doc_id}`}>
          <Calendar size={10}/> Expires {new Date(d.expires_on).toLocaleDateString()}
          {days != null && (days <= 0
            ? <span> · EXPIRED</span>
            : <span> · {days}d left</span>)}
        </div>
      )}
      <div className="flex gap-1 mt-1">
        <Button onClick={onView}  className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[10px] h-7 px-2 flex-1" data-testid={`locker-doc-view-${d.doc_id}`}><Eye size={11} className="mr-1"/> Open</Button>
        <Button onClick={onEdit}  className="rounded-full bg-white/10 hover:bg-white/20 text-white font-black text-[10px] h-7 px-2" data-testid={`locker-doc-edit-${d.doc_id}`}>Edit</Button>
        <Button onClick={onShare} className="rounded-full bg-sky-500 hover:bg-sky-600 text-white font-black text-[10px] h-7 px-2" data-testid={`locker-doc-share-${d.doc_id}`}><Share2 size={11}/></Button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
function PinModal({ mode, settings, onClose, onDone }) {
  const [pin, setPin] = useState("");
  const [current, setCurrent] = useState("");
  const [len, setLen] = useState(settings?.pin_length || 4);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "set") {
        await api.post("/locker/settings/pin", { pin, current_pin: settings?.pin_enabled ? current : undefined });
        toast.success("Locker PIN set");
        onDone(pin);
      } else if (mode === "clear") {
        await api.delete("/locker/settings/pin", { data: { pin } });
        toast.success("Locker PIN removed");
        onDone("");
      } else {
        // unlock — verify then keep it in-memory
        await api.post("/locker/settings/pin/verify", { pin });
        onDone(pin);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "PIN action failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose} data-testid="pin-modal">
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-slate-900 border border-white/10 p-5 w-full max-w-sm space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-white text-sm inline-flex items-center gap-1"><KeyRound size={13}/> {mode === "set" ? "Set Locker PIN" : mode === "clear" ? "Remove Locker PIN" : "Unlock Locker"}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white text-xs"><X size={14}/></button>
        </div>
        {mode === "set" && (
          <div className="inline-flex rounded-full bg-white/5 p-1 text-xs font-black">
            <button onClick={() => setLen(4)} className={`rounded-full px-3 py-1.5 ${len === 4 ? "bg-white text-slate-900" : "text-white/70"}`} data-testid="pin-len-4">4 digits</button>
            <button onClick={() => setLen(6)} className={`rounded-full px-3 py-1.5 ${len === 6 ? "bg-white text-slate-900" : "text-white/70"}`} data-testid="pin-len-6">6 digits</button>
          </div>
        )}
        {mode === "set" && settings?.pin_enabled && (
          <div>
            <label className="text-[10px] uppercase font-black text-white/60">Current PIN</label>
            <Input value={current} onChange={e => setCurrent(e.target.value.replace(/\D/g, ""))}
                   maxLength={8} type="password"
                   className="rounded-full bg-white/5 border-white/10 text-white mt-1" data-testid="pin-current-input"/>
          </div>
        )}
        <div>
          <label className="text-[10px] uppercase font-black text-white/60">{mode === "set" ? `New ${len}-digit PIN` : "Enter PIN"}</label>
          <div className="relative">
            <Input value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ""))}
                   maxLength={mode === "set" ? len : 8} type={show ? "text" : "password"}
                   className="rounded-full bg-white/5 border-white/10 text-white mt-1 pr-10 tracking-widest" data-testid="pin-input"/>
            <button onClick={() => setShow(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50">
              {show ? <EyeOff size={13}/> : <Eye size={13}/>}
            </button>
          </div>
        </div>
        <Button disabled={busy || pin.length < 4 || (mode === "set" && pin.length !== len)}
                onClick={submit}
                className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[11px] w-full" data-testid="pin-submit-btn">
          {busy ? <Loader2 size={12} className="animate-spin"/> :
            mode === "set" ? "Save PIN" : mode === "clear" ? "Remove PIN" : "Unlock"}
        </Button>
        {mode === "set" && <div className="text-[10px] text-white/50">This PIN is required to open or download any document. Choose something you'll remember — recovery requires clearing all documents.</div>}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || "");
      resolve(s.includes(",") ? s.split(",", 2)[1] : s);
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function DocEditor({ doc, categories, onClose, onSaved }) {
  const isNew = !!doc.__new;
  const [category, setCategory] = useState(doc.category || categories[0]?.key);
  const [title, setTitle]       = useState(doc.title || "");
  const [metadata, setMetadata] = useState(doc.metadata || {});
  const [expires, setExpires]   = useState(doc.expires_on || "");
  const [tags, setTags]         = useState((doc.tags || []).join(", "));
  const [attachments, setAttachments] = useState(doc.attachments || []);
  const [busy, setBusy] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);
  const fileRef = useRef(null);
  const catSpec = categories.find(c => c.key === category);

  const onFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const next = [...attachments];
    for (const f of list) {
      if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name} exceeds 5 MB`); continue; }
      if (next.length >= 4)          { toast.error("Max 4 files per document");   break;   }
      try {
        const b64 = await fileToBase64(f);
        next.push({ name: f.name, content_type: f.type || "application/octet-stream", base64: b64, size_bytes: f.size });
      } catch { toast.error(`Failed to read ${f.name}`); }
    }
    setAttachments(next);
  };

  const runOcr = async () => {
    const first = attachments[0];
    if (!first || !first.base64) { toast.error("Add an image first"); return; }
    setOcrBusy(true);
    try {
      const r = await api.post("/locker/ocr", { image_base64: first.base64, category });
      const md = r.data.metadata || {};
      setMetadata(m => ({ ...m, ...md }));
      if (md.expires_on) setExpires(md.expires_on);
      if (md.title && !title) setTitle(md.title);
      toast.success(`Auto-filled ${Object.keys(md).length} field(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "OCR failed"); }
    finally { setOcrBusy(false); }
  };

  const save = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      const payload = {
        category, title,
        metadata,
        expires_on: expires || null,
        tags: tags.split(",").map(t => t.trim()).filter(Boolean),
        attachments: attachments.map(a => ({
          name: a.name, content_type: a.content_type, base64: a.base64,
        })).filter(a => a.base64),
      };
      if (isNew) await api.post("/locker/documents", payload);
      else       await api.patch(`/locker/documents/${doc.doc_id}`, payload);
      toast.success(isNew ? "Document saved to locker" : "Document updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 grid place-items-end sm:place-items-center p-2 sm:p-4"
      style={{ zIndex: "var(--z-modal)" }}
      onClick={onClose}
      data-testid="doc-editor"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="rounded-3xl bg-slate-900 border border-white/10 p-5 w-full max-w-lg space-y-3 overflow-y-auto"
        style={{
          maxHeight: "calc(95vh - var(--f2n-safe-bottom) - var(--f2n-bottom-nav-h))",
          paddingBottom: "calc(1.25rem + var(--f2n-safe-bottom))",
        }}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-black text-white text-sm">{isNew ? "Add to Locker" : "Edit document"}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={16}/></button>
        </div>

        <div>
          <label className="text-[10px] uppercase font-black text-white/60">Category</label>
          <select value={category} onChange={e => { setCategory(e.target.value); setMetadata({}); }}
                  className="w-full mt-1 rounded-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2"
                  data-testid="doc-category-select">
            {categories.map(c => <option key={c.key} value={c.key} className="bg-slate-900">{c.group} — {c.label}</option>)}
          </select>
        </div>

        <div>
          <label className="text-[10px] uppercase font-black text-white/60">Title</label>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. My Passport, LIC Jeevan Anand"
                 className="rounded-full bg-white/5 border-white/10 text-white mt-1" data-testid="doc-title-input"/>
        </div>

        {catSpec && (
          <div className="grid grid-cols-2 gap-2">
            {catSpec.fields.map(f => f === "expires_on" ? null : (
              <SensitiveField key={f} name={f} value={metadata[f] ?? ""}
                              sensitive={(catSpec.sensitive_fields || []).includes(f)}
                              placeholder={FIELD_PLACEHOLDERS[f]}
                              onChange={v => setMetadata(m => ({ ...m, [f]: v }))}/>
            ))}
            <div className="col-span-2">
              <label className="text-[10px] uppercase font-black text-white/60">Expires on</label>
              <Input type="date" value={expires ? expires.slice(0, 10) : ""} onChange={e => setExpires(e.target.value)}
                     className="rounded-full bg-white/5 border-white/10 text-white mt-1 text-sm" data-testid="doc-expires-input"/>
            </div>
          </div>
        )}

        <div>
          <label className="text-[10px] uppercase font-black text-white/60">Tags (comma-separated)</label>
          <Input value={tags} onChange={e => setTags(e.target.value)} placeholder="primary, family, urgent"
                 className="rounded-full bg-white/5 border-white/10 text-white mt-1 text-sm" data-testid="doc-tags-input"/>
        </div>

        <div>
          <label className="text-[10px] uppercase font-black text-white/60">Files (PDF / images, ≤ 5 MB each, up to 4)</label>
          <div className="flex gap-2 mt-1 items-center">
            <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple onChange={e => onFiles(e.target.files)}
                   className="hidden" data-testid="doc-file-input"/>
            <Button onClick={() => fileRef.current?.click()} className="rounded-full bg-white/10 hover:bg-white/20 text-white font-black text-[11px] h-8" data-testid="doc-upload-btn">
              <Upload size={12} className="mr-1"/> Upload
            </Button>
            {attachments.length > 0 && attachments[0].base64 && (
              <Button onClick={runOcr} disabled={ocrBusy} className="rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-black text-[11px] h-8" data-testid="doc-ocr-btn">
                {ocrBusy ? <Loader2 size={12} className="animate-spin"/> : <><Sparkles size={12} className="mr-1"/> Auto-fill from image</>}
              </Button>
            )}
          </div>
          {attachments.length > 0 && (
            <div className="mt-2 space-y-1">
              {attachments.map((a, i) => (
                <div key={i} className="flex items-center gap-2 bg-white/5 rounded-xl px-2 py-1" data-testid={`doc-att-${i}`}>
                  <FileText size={12} className="text-emerald-400"/>
                  <div className="flex-1 truncate text-[11px] text-white">{a.name}</div>
                  <div className="text-[10px] text-white/50">{Math.round((a.size_bytes || 0) / 1024)} KB</div>
                  <button onClick={() => setAttachments(list => list.filter((_, idx) => idx !== i))} className="text-white/50 hover:text-rose-400"><Trash2 size={11}/></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-2 pt-2 border-t border-white/10">
          <Button onClick={onClose} className="rounded-full bg-white/10 hover:bg-white/20 text-white font-black text-[11px] flex-1">Cancel</Button>
          <Button disabled={busy} onClick={save} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[11px] flex-1" data-testid="doc-save-btn">
            {busy ? <Loader2 size={12} className="animate-spin"/> : (isNew ? "Save to locker" : "Update")}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
function DocViewer({ d, onClose, onDeleted }) {
  const [showClaims, setShowClaims] = useState(false);
  const [claims, setClaims] = useState([]);
  const [newClaim, setNewClaim] = useState(null);
  const [previewIdx, setPreviewIdx] = useState(null);
  const [blobUrls, setBlobUrls] = useState({});

  // Build blob URLs lazily so we can render PDFs in an iframe + images in an img
  useEffect(() => {
    const urls = {};
    (d.attachments || []).forEach((a, i) => {
      try {
        const bin = atob(a.base64);
        const bytes = new Uint8Array(bin.length);
        for (let j = 0; j < bin.length; j++) bytes[j] = bin.charCodeAt(j);
        urls[i] = URL.createObjectURL(new Blob([bytes], { type: a.content_type || "application/octet-stream" }));
      } catch { /* skip */ }
    });
    setBlobUrls(urls);
    return () => { Object.values(urls).forEach(u => URL.revokeObjectURL(u)); };
  }, [d.doc_id]);   // eslint-disable-line

  const insuranceKeys = ["lic", "term_insurance", "health_insurance", "vehicle_insurance"];
  const canClaim = insuranceKeys.includes(d.category);

  const loadClaims = async () => {
    try { const r = await api.get(`/locker/claims/${d.doc_id}`); setClaims(r.data || []); }
    catch { /* silent */ }
  };
  useEffect(() => { if (showClaims) loadClaims(); /* eslint-disable-next-line */ }, [showClaims]);

  const remove = async () => {
    if (!confirm("Delete this document permanently?")) return;
    try { await api.delete(`/locker/documents/${d.doc_id}`); toast.success("Deleted"); onDeleted(); }
    catch { toast.error("Delete failed"); }
  };

  const download = (att) => {
    try {
      const bin = atob(att.base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: att.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = att.name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error("Download failed"); }
  };

  const saveClaim = async () => {
    try {
      await api.post("/locker/claims", { document_id: d.doc_id, ...newClaim });
      setNewClaim(null); loadClaims(); toast.success("Claim logged");
    } catch (e) { toast.error(e?.response?.data?.detail || "Claim save failed"); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 grid place-items-end sm:place-items-center p-2 sm:p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose} data-testid="doc-viewer">
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-slate-900 border border-white/10 p-5 w-full max-w-lg space-y-3 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase text-emerald-300 font-black">{d.group} · {d.category}</div>
            <h3 className="font-black text-white text-lg" data-testid="viewer-title">{d.title}</h3>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={16}/></button>
        </div>

        {d.expires_on && (
          <div className={`text-[11px] font-black inline-flex items-center gap-1 ${urgencyClass(d.expiring_in_days)}`}>
            <Calendar size={11}/> Expires {new Date(d.expires_on).toLocaleDateString()}
            {d.expiring_in_days != null && (d.expiring_in_days <= 0 ? " · EXPIRED" : ` · ${d.expiring_in_days}d left`)}
          </div>
        )}

        {d.metadata && Object.keys(d.metadata).length > 0 && (
          <div className="rounded-2xl bg-white/5 border border-white/10 p-3 space-y-1" data-testid="viewer-metadata">
            {Object.entries(d.metadata).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2 text-[11px]">
                <span className="text-white/50">{k.replace(/_/g, " ")}</span>
                <span className="text-white font-black text-right break-all">{String(v)}</span>
              </div>
            ))}
          </div>
        )}

        {d.attachments?.length > 0 && (
          <div className="space-y-2" data-testid="viewer-attachments">
            <div className="text-[10px] uppercase font-black text-white/60">Files</div>
            {d.attachments.map((a, i) => {
              const isImg = (a.content_type || "").startsWith("image/");
              const isPdf = (a.content_type || "") === "application/pdf" || (a.name || "").toLowerCase().endsWith(".pdf");
              const url = blobUrls[i];
              const isOpen = previewIdx === i;
              return (
                <div key={i} className="rounded-2xl bg-white/5 border border-white/10" data-testid={`viewer-att-${i}`}>
                  <div className="flex items-center gap-2 px-2 py-1.5">
                    <FileText size={12} className="text-emerald-400"/>
                    <div className="flex-1 truncate text-[11px] text-white">{a.name}</div>
                    <div className="text-[10px] text-white/50">{Math.round((a.size_bytes || 0) / 1024)} KB</div>
                    {(isImg || isPdf) && url && (
                      <Button onClick={() => setPreviewIdx(isOpen ? null : i)}
                              className="rounded-full bg-sky-500 text-white font-black text-[10px] h-7 px-2" data-testid={`viewer-preview-${i}`}>
                        {isOpen ? <><EyeOff size={11} className="mr-1"/> Hide</> : <><Eye size={11} className="mr-1"/> Preview</>}
                      </Button>
                    )}
                    <Button onClick={() => download(a)} className="rounded-full bg-emerald-500 text-slate-900 font-black text-[10px] h-7 px-2" data-testid={`viewer-download-${i}`}>
                      <Download size={11} className="mr-1"/> Save
                    </Button>
                  </div>
                  {isOpen && isImg && url && (
                    <div className="p-2 bg-black/40" data-testid={`viewer-img-${i}`}>
                      <img src={url} alt={a.name} className="w-full max-h-[60vh] object-contain rounded-xl"/>
                    </div>
                  )}
                  {isOpen && isPdf && url && (
                    <iframe src={url} title={a.name}
                            className="w-full h-[70vh] rounded-b-2xl border-0 bg-white"
                            data-testid={`viewer-pdf-${i}`}/>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {canClaim && (
          <div className="rounded-2xl bg-fuchsia-500/10 border border-fuchsia-400/20 p-3" data-testid="viewer-claims">
            <button onClick={() => setShowClaims(x => !x)} className="text-[10px] uppercase font-black text-fuchsia-300 inline-flex items-center gap-1">
              <Sparkles size={11}/> Claim tracker {showClaims ? "▲" : "▼"}
            </button>
            {showClaims && (
              <div className="mt-2 space-y-2">
                {claims.length === 0 && <div className="text-[11px] text-white/50">No claims logged yet.</div>}
                {claims.map(c => (
                  <div key={c.claim_id} className="bg-white/5 rounded-xl p-2 text-[11px] flex flex-col gap-0.5" data-testid={`claim-row-${c.claim_id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-black text-white">{c.claim_number || c.claim_id}</span>
                      <span className={`rounded-full text-[9px] font-black uppercase px-2 py-0.5 ${c.status === "paid" || c.status === "approved" ? "bg-emerald-500/20 text-emerald-300" : c.status === "rejected" ? "bg-rose-500/20 text-rose-300" : "bg-amber-500/20 text-amber-300"}`}>{c.status}</span>
                    </div>
                    <div className="text-white/60">Filed {c.filed_on} · Incident {c.incident_on || "—"} · Amount ₹{c.amount ?? "—"}</div>
                    {c.notes && <div className="text-white/50">{c.notes}</div>}
                  </div>
                ))}
                {newClaim ? (
                  <div className="bg-white/5 rounded-xl p-2 space-y-1">
                    <Input placeholder="Claim number" value={newClaim.claim_number || ""} onChange={e => setNewClaim({ ...newClaim, claim_number: e.target.value })} className="rounded-full bg-white/5 border-white/10 text-white text-xs" data-testid="claim-number-input"/>
                    <div className="grid grid-cols-2 gap-1">
                      <Input type="date" value={newClaim.filed_on || ""} onChange={e => setNewClaim({ ...newClaim, filed_on: e.target.value })} className="rounded-full bg-white/5 border-white/10 text-white text-xs" data-testid="claim-filed-input"/>
                      <Input type="number" placeholder="Amount" value={newClaim.amount || ""} onChange={e => setNewClaim({ ...newClaim, amount: parseFloat(e.target.value) || undefined })} className="rounded-full bg-white/5 border-white/10 text-white text-xs" data-testid="claim-amount-input"/>
                    </div>
                    <select value={newClaim.status} onChange={e => setNewClaim({ ...newClaim, status: e.target.value })}
                            className="w-full rounded-full bg-white/5 border border-white/10 text-white text-xs px-3 py-1.5" data-testid="claim-status-select">
                      {["filed", "processing", "approved", "rejected", "paid"].map(s => <option key={s} value={s} className="bg-slate-900">{s}</option>)}
                    </select>
                    <div className="flex gap-1">
                      <Button onClick={saveClaim} className="rounded-full bg-emerald-500 text-slate-900 font-black text-[10px] h-7 flex-1" data-testid="claim-save-btn">Save claim</Button>
                      <Button onClick={() => setNewClaim(null)} className="rounded-full bg-white/10 text-white font-black text-[10px] h-7">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <Button onClick={() => setNewClaim({ status: "filed", filed_on: new Date().toISOString().slice(0, 10) })}
                          className="rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-black text-[11px] h-7 w-full" data-testid="claim-new-btn">
                    <Plus size={11} className="mr-1"/> Log a claim
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2 pt-2 border-t border-white/10">
          <Button onClick={remove} className="rounded-full bg-rose-500 hover:bg-rose-600 text-white font-black text-[11px]" data-testid="viewer-delete-btn"><Trash2 size={12} className="mr-1"/> Delete</Button>
          <Button onClick={onClose} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[11px] flex-1">Close</Button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
function ShareBuilder({ ctx, docs, onClose, onCreated }) {
  const [selected, setSelected] = useState(() => ctx.__one ? [ctx.__one] : []);
  const [nomineeName, setNomineeName] = useState("");
  const [nomineeEmail, setNomineeEmail] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [hours, setHours] = useState(168);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const toggle = (id) => {
    setSelected(sel => sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);
  };

  const create = async () => {
    if (!selected.length) { toast.error("Pick at least one document"); return; }
    setBusy(true);
    try {
      const r = await api.post("/locker/shares", {
        document_ids: selected,
        nominee_name: nomineeName || undefined,
        nominee_email: nomineeEmail || undefined,
        passphrase: passphrase || undefined,
        expires_hrs: hours,
      });
      setResult({ ...r.data, url: `${window.location.origin}/locker/share/${r.data.share_id}` });
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create share"); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    if (!result?.url) return;
    try { await navigator.clipboard.writeText(result.url); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 1500); }
    catch { /* silent */ }
  };

  return (
    <div className="fixed inset-0 bg-black/80 grid place-items-end sm:place-items-center p-2 sm:p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose} data-testid="share-builder">
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-slate-900 border border-white/10 p-5 w-full max-w-md space-y-3 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-white text-sm inline-flex items-center gap-1"><Users size={13}/> Share with nominee / family</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={14}/></button>
        </div>

        {result ? (
          <div className="space-y-2">
            <div className="text-[11px] text-emerald-300 font-black">Share link created</div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-2 text-[11px] text-white/80 break-all font-mono" data-testid="share-result-url">{result.url}</div>
            <div className="flex gap-2">
              <Button onClick={copy} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[11px] flex-1" data-testid="share-copy-btn">
                {copied ? <><Check size={12} className="mr-1"/> Copied</> : <><Copy size={12} className="mr-1"/> Copy link</>}
              </Button>
              <Button onClick={onClose} className="rounded-full bg-white/10 text-white font-black text-[11px]">Done</Button>
            </div>
            <a href={`${API}/locker/shares/${result.share_id}/poster.pdf`}
               target="_blank" rel="noopener"
               className="mt-1 rounded-full bg-white/10 hover:bg-white/20 text-white text-[11px] font-black h-9 inline-flex items-center justify-center gap-1"
               data-testid="share-poster-btn">
              🖨️ Poster · English (A4 PDF)
            </a>
            <a href={`${API}/locker/shares/${result.share_id}/poster.pdf?lang=hi_en`}
               target="_blank" rel="noopener"
               className="rounded-full bg-white/10 hover:bg-white/20 text-white text-[11px] font-black h-9 inline-flex items-center justify-center gap-1"
               data-testid="share-poster-hi-btn">
              🇮🇳 पोस्टर · हिन्दी + English
            </a>
            <SendPosterButton shareId={result.share_id} defaultTo={result.nominee_email}/>
            <div className="text-[10px] text-white/50">
              Send this to your nominee. It exposes only the selected {result.document_ids?.length} document(s){result.passphrase?.required ? " and needs the passphrase you set" : ""}. Expires {new Date(result.expires_at).toLocaleString()}. You can revoke it anytime from the locker.
            </div>
          </div>
        ) : (
          <>
            <div>
              <div className="text-[10px] uppercase font-black text-white/60 mb-1">Documents ({selected.length} selected)</div>
              <div className="max-h-40 overflow-y-auto space-y-1 rounded-2xl bg-white/5 border border-white/10 p-2">
                {docs.length === 0 && <div className="text-[11px] text-white/50">Add a document first.</div>}
                {docs.map(d => (
                  <label key={d.doc_id} className="flex items-center gap-2 text-[11px] text-white cursor-pointer" data-testid={`share-pick-${d.doc_id}`}>
                    <input type="checkbox" checked={selected.includes(d.doc_id)} onChange={() => toggle(d.doc_id)} className="accent-emerald-500"/>
                    <span className="flex-1 truncate">{d.title} <span className="text-white/40">· {d.category}</span></span>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase font-black text-white/60">Nominee name</label>
                <Input value={nomineeName} onChange={e => setNomineeName(e.target.value)} placeholder="Spouse, Parent…"
                       className="rounded-full bg-white/5 border-white/10 text-white text-xs mt-1" data-testid="share-nominee-name"/>
              </div>
              <div>
                <label className="text-[10px] uppercase font-black text-white/60">Email (optional)</label>
                <Input value={nomineeEmail} onChange={e => setNomineeEmail(e.target.value)} placeholder="nominee@example.com"
                       className="rounded-full bg-white/5 border-white/10 text-white text-xs mt-1" data-testid="share-nominee-email"/>
              </div>
              <div>
                <label className="text-[10px] uppercase font-black text-white/60">Passphrase (optional)</label>
                <Input value={passphrase} onChange={e => setPassphrase(e.target.value)} placeholder="4-32 chars"
                       className="rounded-full bg-white/5 border-white/10 text-white text-xs mt-1" data-testid="share-passphrase"/>
              </div>
              <div>
                <label className="text-[10px] uppercase font-black text-white/60">Expires in (hours)</label>
                <Input type="number" min={1} max={720} value={hours} onChange={e => setHours(parseInt(e.target.value) || 1)}
                       className="rounded-full bg-white/5 border-white/10 text-white text-xs mt-1" data-testid="share-hours"/>
              </div>
            </div>
            <Button disabled={busy || !selected.length} onClick={create}
                    className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[11px] w-full" data-testid="share-create-btn">
              {busy ? <Loader2 size={12} className="animate-spin"/> : "Create share link"}
            </Button>
            <p className="text-[10px] text-white/50 leading-snug pt-1" data-testid="share-track-disclosure">
              🔔 For your safety, we log every open of this link and notify you (in-app + email) so
              you know the emergency handoff worked. Duplicate opens within 30 min are suppressed.
            </p>
          </>
        )}
      </div>
    </div>
  );
}



// ───────────────────────────────────────────────────────────────────────
// Cloud backup — encrypt every document + attachment with a user-chosen
// password and stream the ciphertext as a single `.f2n` file. Users then
// upload that one file to Drive / iCloud / OneDrive at their leisure.
function CloudBackupDialog({ onClose }) {
  const [tab,  setTab]  = useState("export");    // export | restore | auto
  return (
    <div className="fixed inset-0 bg-slate-900/90 backdrop-blur z-[900] grid place-items-center p-4"
         onClick={onClose} data-testid="locker-backup-dialog">
      <div onClick={e => e.stopPropagation()}
           className="w-full max-w-lg rounded-3xl bg-gradient-to-br from-slate-800 to-slate-900 border border-white/10 p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Download size={16} className="text-emerald-300"/>
          <div className="text-white font-black text-[14px]">Locker Backup</div>
          <button onClick={onClose} className="ml-auto text-white/50 hover:text-white text-[16px]" aria-label="Close">×</button>
        </div>
        <div className="flex gap-1 rounded-full bg-white/5 p-1">
          {[
            { id: "export",  label: "Export" },
            { id: "restore", label: "Restore" },
            { id: "auto",    label: "Auto (daily)" },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
                    data-testid={`backup-tab-${t.id}`}
                    className={`flex-1 rounded-full text-[10px] font-black uppercase h-7 ${tab === t.id ? "bg-emerald-500 text-slate-900" : "text-white/70 hover:text-white"}`}>
              {t.label}
            </button>
          ))}
        </div>
        {tab === "export"  && <BackupExportPanel onClose={onClose}/>}
        {tab === "restore" && <BackupRestorePanel onClose={onClose}/>}
        {tab === "auto"    && <BackupAutoPanel/>}
      </div>
    </div>
  );
}

function BackupExportPanel({ onClose }) {
  const [pw,   setPw]   = useState("");
  const [pw2,  setPw2]  = useState("");
  const [busy, setBusy] = useState(false);
  const strong = pw.length >= 12 && /[A-Z]/.test(pw) && /[0-9]/.test(pw);
  const match  = pw && pw === pw2;

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (!match)        { toast.error("Passwords do not match.");                return; }
    setBusy(true);
    try {
      const r = await api.post("/locker/backup/export",
        { password: pw }, { responseType: "blob" });
      const stamp = new Date().toISOString().replace(/[-:]/g, "").slice(0, 13);
      const filename = `f2n-locker-${stamp}.f2n`;
      const url  = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = filename; link.click();
      URL.revokeObjectURL(url);
      const docs = r.headers["x-locker-docs"] || "?";
      toast.success(`Encrypted backup of ${docs} documents downloaded. Upload to your cloud drive.`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Backup failed. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="text-[11px] text-white/60 leading-snug">
        We'll wrap every document (attachments, posters, metadata) into one <span className="font-mono text-white/80">.f2n</span> file
        encrypted with your password. Nothing is uploaded — download it and put it on
        Google Drive, iCloud, OneDrive or a USB stick. Forget the password and the file is unrecoverable.
      </div>
      <div>
        <label className="text-[10px] uppercase font-black text-white/60">Password (8+ chars)</label>
        <Input type="password" value={pw} onChange={e => setPw(e.target.value)}
               autoComplete="new-password"
               className="rounded-full bg-white/5 border-white/10 text-white mt-1"
               data-testid="locker-backup-pw"/>
        <div className="text-[10px] mt-1"
             data-testid="locker-backup-pw-strength">
          {pw.length === 0 ? <span className="text-white/40">Tip: 12+ chars, a number and an uppercase letter for a strong key.</span>
           : strong ? <span className="text-emerald-300">Strong password ✓</span>
           : <span className="text-amber-300">Consider a longer password with mixed case and a number.</span>}
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase font-black text-white/60">Repeat password</label>
        <Input type="password" value={pw2} onChange={e => setPw2(e.target.value)}
               autoComplete="new-password"
               className="rounded-full bg-white/5 border-white/10 text-white mt-1"
               data-testid="locker-backup-pw2"/>
        {pw2 && !match && (
          <div className="text-[10px] text-rose-300 mt-1" data-testid="locker-backup-pw-mismatch">Passwords do not match.</div>
        )}
      </div>
      <Button type="submit" disabled={busy || !match || pw.length < 8}
              data-testid="locker-backup-download-btn"
              className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[11px] h-9 w-full disabled:opacity-40">
        {busy ? "Encrypting…" : "Encrypt & download"}
      </Button>
    </form>
  );
}

function BackupRestorePanel({ onClose }) {
  const [file, setFile] = useState(null);
  const [pw,   setPw]   = useState("");
  const [strategy, setStrategy] = useState("skip_existing");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file)         { toast.error("Choose a .f2n backup file first."); return; }
    if (pw.length < 8) { toast.error("Enter the password used when the backup was made.");  return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("password", pw);
      fd.append("strategy", strategy);
      fd.append("file", file);
      const r = await api.post("/locker/backup/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      const { restored, skipped, overwritten, duplicated } = r.data;
      toast.success(`Restore complete · ${restored} new · ${skipped} skipped · ${overwritten} overwritten · ${duplicated} duplicated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed. Check the password and try again.");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="text-[11px] text-white/60 leading-snug">
        Pick the encrypted <span className="font-mono text-white/80">.f2n</span> file you exported earlier.
        We'll decrypt it in-memory and merge the documents into your locker — nothing is uploaded to any third party.
      </div>
      <div>
        <label className="text-[10px] uppercase font-black text-white/60">Backup file (.f2n)</label>
        <input type="file" accept=".f2n,application/octet-stream"
               onChange={e => setFile(e.target.files?.[0] || null)}
               className="mt-1 block w-full text-[11px] text-white file:mr-2 file:rounded-full file:border-0 file:bg-emerald-500 file:text-slate-900 file:px-3 file:py-1.5 file:font-black file:cursor-pointer"
               data-testid="locker-restore-file"/>
        {file && <div className="text-[10px] text-white/50 mt-1">{file.name} · {(file.size/1024).toFixed(1)} KB</div>}
      </div>
      <div>
        <label className="text-[10px] uppercase font-black text-white/60">Password</label>
        <Input type="password" value={pw} onChange={e => setPw(e.target.value)}
               autoComplete="current-password"
               className="rounded-full bg-white/5 border-white/10 text-white mt-1"
               data-testid="locker-restore-pw"/>
      </div>
      <div>
        <label className="text-[10px] uppercase font-black text-white/60">If a document already exists</label>
        <div className="mt-1 grid grid-cols-3 gap-1" data-testid="locker-restore-strategy">
          {[
            { id: "skip_existing", label: "Skip"       },
            { id: "overwrite",     label: "Overwrite" },
            { id: "duplicate",     label: "Duplicate" },
          ].map(o => (
            <button type="button" key={o.id} onClick={() => setStrategy(o.id)}
                    data-testid={`locker-restore-strategy-${o.id}`}
                    className={`rounded-full text-[10px] font-black h-8 ${strategy === o.id ? "bg-sky-500 text-slate-900" : "bg-white/5 text-white/70 hover:bg-white/10"}`}>
              {o.label}
            </button>
          ))}
        </div>
        <div className="text-[10px] text-white/50 mt-1">
          {strategy === "skip_existing" && "Keep the local doc; ignore the backup copy. (Safest.)"}
          {strategy === "overwrite"     && "Backup wins — the local doc is replaced with the backup version."}
          {strategy === "duplicate"     && "Keep both — the backup is inserted with a new ID."}
        </div>
      </div>
      {result && (
        <div className="rounded-2xl bg-emerald-500/10 border border-emerald-400/30 p-2 text-[11px] text-emerald-100" data-testid="locker-restore-result">
          Restore complete — <b>{result.restored}</b> new · <b>{result.skipped}</b> skipped · <b>{result.overwritten}</b> overwritten · <b>{result.duplicated}</b> duplicated (of {result.total_docs} in the backup).
        </div>
      )}
      <div className="flex gap-2">
        <Button type="button" onClick={onClose}
                className="rounded-full bg-white/10 hover:bg-white/20 text-white font-black text-[11px] h-9 flex-1">
          {result ? "Close" : "Cancel"}
        </Button>
        <Button type="submit" disabled={busy || !file || pw.length < 8}
                data-testid="locker-restore-submit-btn"
                className="rounded-full bg-sky-500 hover:bg-sky-600 text-slate-900 font-black text-[11px] h-9 flex-1 disabled:opacity-40">
          {busy ? "Decrypting…" : "Decrypt & restore"}
        </Button>
      </div>
    </form>
  );
}

function BackupAutoPanel() {
  const [prefs, setPrefs] = useState(null);   // { enabled, last_run_at }
  const [list,  setList]  = useState(null);   // [{backup_id, created_at, docs}]
  const [busy,  setBusy]  = useState(false);
  const [pw,    setPw]    = useState("");
  const [downloadFor, setDownloadFor] = useState(null); // backup_id
  const [driveEnabled, setDriveEnabled] = useState(false);

  const load = async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/locker/backup/auto/settings"),
        api.get("/locker/backup/auto/list"),
      ]);
      setPrefs(s.data);
      setList(l.data.backups || []);
      setDriveEnabled(!!s.data.drive_configured);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      const r = await api.post("/locker/backup/auto/settings",
        { enabled: !(prefs?.enabled) });
      setPrefs(r.data);
      toast.success(r.data.enabled ? "Daily auto-backup ON — first snapshot within an hour." : "Auto-backup OFF.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update settings.");
    } finally { setBusy(false); }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/locker/backup/auto/run-now");
      toast.success(`Snapshot ready · ${r.data?.stats?.documents ?? "?"} docs`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Snapshot failed.");
    } finally { setBusy(false); }
  };

  const download = async (backupId) => {
    if (pw.length < 8) { toast.error("Enter a password (8+ chars) to encrypt this download."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/locker/backup/auto/${backupId}/download`,
        { password: pw }, { responseType: "blob" });
      const filename = `f2n-auto-${backupId}.f2n`;
      const url  = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = filename; link.click();
      URL.revokeObjectURL(url);
      toast.success("Encrypted snapshot downloaded.");
      setDownloadFor(null); setPw("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="text-[11px] text-white/60 leading-snug">
        Turn this on and Find2Near creates a fresh server-side snapshot of your locker every day (retained 7 days).
        You can download any snapshot as an encrypted <span className="font-mono text-white/80">.f2n</span> file by
        entering a password — nothing is decrypted until you type it, and we don't store your password.
      </div>
      <div className="flex items-center gap-2 rounded-2xl bg-white/5 border border-white/10 p-3">
        <div className="flex-1">
          <div className="text-[12px] font-black text-white" data-testid="auto-backup-status">
            {prefs?.enabled ? "Daily auto-backup: ON" : "Daily auto-backup: OFF"}
          </div>
          <div className="text-[10px] text-white/50">
            {prefs?.last_run_at ? `Last snapshot: ${new Date(prefs.last_run_at).toLocaleString()}` : "No snapshots yet."}
          </div>
        </div>
        <Button onClick={toggle} disabled={busy}
                data-testid="auto-backup-toggle"
                className={`rounded-full font-black text-[10px] h-8 px-4 ${prefs?.enabled ? "bg-rose-500 hover:bg-rose-600 text-white" : "bg-emerald-500 hover:bg-emerald-600 text-slate-900"} disabled:opacity-40`}>
          {prefs?.enabled ? "Turn off" : "Turn on"}
        </Button>
      </div>
      {prefs?.enabled && (
        <Button onClick={runNow} disabled={busy}
                data-testid="auto-backup-run-now"
                className="rounded-full bg-white/10 hover:bg-white/20 text-white font-black text-[10px] h-8 w-full">
          {busy ? "Working…" : "Run one now"}
        </Button>
      )}
      <div className="rounded-2xl bg-white/5 border border-white/10 p-2">
        <div className="text-[10px] uppercase font-black text-white/50 mb-1">Snapshots (last 7 days)</div>
        {(list?.length ?? 0) === 0 ? (
          <div className="text-[11px] text-white/40 py-2 text-center">No snapshots yet — turn on auto-backup or run one now.</div>
        ) : (
          <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="auto-backup-list">
            {list.map(b => (
              <div key={b.backup_id} className="rounded-xl bg-slate-900/60 p-2 flex items-center gap-2" data-testid={`auto-backup-row-${b.backup_id}`}>
                <div className="flex-1">
                  <div className="text-[11px] font-black text-white">{new Date(b.created_at).toLocaleString()}</div>
                  <div className="text-[9.5px] text-white/50">{b.docs} docs · {(b.bytes/1024).toFixed(1)} KB</div>
                </div>
                <button onClick={() => setDownloadFor(downloadFor === b.backup_id ? null : b.backup_id)}
                        className="rounded-full bg-sky-500/20 hover:bg-sky-500/30 text-sky-200 font-black text-[10px] px-3 py-1"
                        data-testid={`auto-backup-download-${b.backup_id}`}>
                  {downloadFor === b.backup_id ? "Cancel" : "Download"}
                </button>
              </div>
            ))}
          </div>
        )}
        {downloadFor && (
          <div className="mt-2 rounded-xl bg-sky-500/10 border border-sky-400/30 p-2 space-y-2">
            <div className="text-[10px] text-sky-200">Password for this download's encryption:</div>
            <Input type="password" value={pw} onChange={e => setPw(e.target.value)}
                   autoComplete="new-password"
                   className="rounded-full bg-white/5 border-white/10 text-white text-[11px] h-8"
                   data-testid="auto-backup-download-pw"/>
            <Button onClick={() => download(downloadFor)} disabled={busy || pw.length < 8}
                    className="rounded-full bg-sky-500 hover:bg-sky-600 text-slate-900 font-black text-[10px] h-8 w-full disabled:opacity-40"
                    data-testid="auto-backup-download-confirm">
              {busy ? "Encrypting…" : "Encrypt & download"}
            </Button>
          </div>
        )}
      </div>
      <div className={`rounded-2xl border p-2 text-[10.5px] leading-snug ${driveEnabled ? "bg-emerald-500/10 border-emerald-400/30 text-emerald-100" : "bg-white/5 border-white/10 text-white/60"}`} data-testid="drive-status">
        {driveEnabled
          ? "Google Drive: connected. Snapshots also upload to your Drive automatically."
          : "Google Drive upload: pending. Once your admin adds Drive credentials to the server, snapshots will also upload to your Drive nightly."}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Shared-with audit panel — full chain of custody for family emergencies.
// Lists every active share the owner has created, expands to show each
// open event (time, device family, hashed IP prefix, notified?).
function ShareAuditPanel({ onClose }) {
  const [shares, setShares]     = useState(null);   // null=loading, [] empty
  const [openMap, setOpenMap]   = useState({});     // share_id → { loaded, opens }
  const [expanded, setExpanded] = useState(null);   // share_id currently expanded
  const [revoking, setRevoking] = useState(null);   // share_id being revoked

  useEffect(() => {
    api.get("/locker/shares/mine")
       .then(r => setShares(r.data || []))
       .catch(() => { setShares([]); toast.error("Couldn't load shares"); });
  }, []);

  const toggle = async (share_id) => {
    if (expanded === share_id) { setExpanded(null); return; }
    setExpanded(share_id);
    if (openMap[share_id]?.loaded) return;
    setOpenMap(m => ({ ...m, [share_id]: { loaded: false, opens: [] } }));
    try {
      const r = await api.get(`/locker/shares/${share_id}/opens`);
      setOpenMap(m => ({ ...m, [share_id]: { loaded: true, opens: r.data?.opens || [] } }));
    } catch {
      setOpenMap(m => ({ ...m, [share_id]: { loaded: true, opens: [] } }));
      toast.error("Couldn't load open history");
    }
  };

  const revoke = async (share_id) => {
    if (!window.confirm("Revoke this share link? The nominee will no longer be able to open it.")) return;
    setRevoking(share_id);
    try {
      await api.delete(`/locker/shares/${share_id}`);
      setShares(list => list.map(s => s.share_id === share_id ? { ...s, revoked: true } : s));
      toast.success("Share revoked");
    } catch { toast.error("Couldn't revoke — try again"); }
    finally { setRevoking(null); }
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };
  const statusChip = (s) => {
    if (s.revoked) return { label: "Revoked", cls: "bg-rose-500/20 text-rose-300" };
    const exp = s.expires_at ? new Date(s.expires_at) : null;
    if (exp && exp < new Date()) return { label: "Expired", cls: "bg-stone-500/30 text-stone-300" };
    return { label: "Active", cls: "bg-emerald-500/20 text-emerald-300" };
  };

  return (
    <div className="fixed inset-0 bg-black/80 grid place-items-end sm:place-items-center p-2 sm:p-4" style={{ zIndex: "var(--z-modal)" }}
         onClick={onClose} data-testid="share-audit-panel">
      <div onClick={e => e.stopPropagation()}
           className="rounded-3xl bg-slate-900 border border-white/10 p-5 w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-black text-white">Shared with</div>
            <div className="text-[11px] text-white/50">Full chain of custody · who opened what & when</div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="audit-close-btn">
            <X size={16}/>
          </button>
        </div>

        {shares === null ? (
          <div className="text-center py-8 text-white/50 text-xs" data-testid="audit-loading">Loading…</div>
        ) : shares.length === 0 ? (
          <div className="text-center py-8 text-white/50 text-xs rounded-2xl border border-dashed border-white/10"
               data-testid="audit-empty">
            You haven't shared any documents yet. Use <span className="font-black text-emerald-300">Share with nominee</span> to create an emergency handoff link.
          </div>
        ) : (
          <div className="space-y-2">
            {shares.map(s => {
              const st = statusChip(s);
              const opens = openMap[s.share_id];
              return (
                <div key={s.share_id}
                     className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden"
                     data-testid={`audit-share-row-${s.share_id}`}>
                  <button onClick={() => toggle(s.share_id)}
                          className="w-full text-left p-3 flex items-start gap-2 hover:bg-white/5"
                          data-testid={`audit-share-toggle-${s.share_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-black text-sm text-white truncate">
                          {s.nominee_name || "Unnamed nominee"}
                        </span>
                        <span className={`rounded-full text-[9px] font-black uppercase px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                        {s.passphrase?.required && (
                          <span className="rounded-full bg-amber-500/20 text-amber-300 text-[9px] font-black uppercase px-2 py-0.5">Passphrase</span>
                        )}
                      </div>
                      <div className="text-[10px] text-white/60 mt-0.5">
                        {(s.document_ids || []).length} doc{(s.document_ids || []).length === 1 ? "" : "s"}
                        {" · "}Created {fmt(s.created_at)}
                        {s.expires_at && <> · Expires {fmt(s.expires_at)}</>}
                      </div>
                      <div className="text-[10px] text-white/50 mt-0.5">
                        Opens: <span className="text-white font-black" data-testid={`audit-count-${s.share_id}`}>{s.view_count || 0}</span>
                        {s.last_viewed_at && <> · Last {fmt(s.last_viewed_at)}</>}
                      </div>
                    </div>
                    <div className="text-white/40 text-xs shrink-0 mt-1">
                      {expanded === s.share_id ? "▲" : "▼"}
                    </div>
                  </button>

                  {expanded === s.share_id && (
                    <div className="border-t border-white/10 p-3 bg-black/20 space-y-2">
                      {!opens?.loaded ? (
                        <div className="text-[11px] text-white/50" data-testid={`audit-opens-loading-${s.share_id}`}>Loading opens…</div>
                      ) : opens.opens.length === 0 ? (
                        <div className="text-[11px] text-white/50" data-testid={`audit-opens-empty-${s.share_id}`}>
                          No opens yet — the nominee hasn't accessed this link.
                        </div>
                      ) : (
                        <div className="space-y-1" data-testid={`audit-opens-list-${s.share_id}`}>
                          {opens.opens.map((o, i) => (
                            <div key={i} className="rounded-lg bg-white/5 px-2 py-1.5 text-[11px] flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0"/>
                              <span className="text-white/80 flex-1">{fmt(o.opened_at)}</span>
                              <span className="rounded-full bg-white/10 text-white/60 text-[9px] font-black px-1.5 py-0.5">{o.device}</span>
                              <span className="text-white/40 font-mono text-[9px]" title="Hashed IP prefix (no raw IP stored)">ip#{o.ip_prefix}</span>
                              {o.notified && <span className="text-emerald-300 text-[9px] font-black">🔔 notified</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      {!s.revoked && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          <Button onClick={() => revoke(s.share_id)}
                                  disabled={revoking === s.share_id}
                                  className="rounded-full bg-rose-500 hover:bg-rose-600 text-white font-black text-[10px] h-7"
                                  data-testid={`audit-revoke-btn-${s.share_id}`}>
                            {revoking === s.share_id
                              ? <><Loader2 size={11} className="animate-spin mr-1"/>Revoking…</>
                              : "Revoke link"}
                          </Button>
                          <a href={`${API}/locker/shares/${s.share_id}/opens.csv`}
                             onClick={(e) => e.stopPropagation()}
                             className="rounded-full bg-white/10 hover:bg-white/20 text-white font-black text-[10px] h-7 px-3 inline-flex items-center gap-1"
                             data-testid={`audit-csv-btn-${s.share_id}`}
                             target="_blank" rel="noopener">
                            📊 Download CSV
                          </a>
                          <a href={`${API}/locker/shares/${s.share_id}/poster.pdf`}
                             onClick={(e) => e.stopPropagation()}
                             className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[10px] h-7 px-3 inline-flex items-center gap-1"
                             data-testid={`audit-poster-btn-${s.share_id}`}
                             target="_blank" rel="noopener">
                            🖨️ Poster (EN)
                          </a>
                          <a href={`${API}/locker/shares/${s.share_id}/poster.pdf?lang=hi_en`}
                             onClick={(e) => e.stopPropagation()}
                             className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-black text-[10px] h-7 px-3 inline-flex items-center gap-1"
                             data-testid={`audit-poster-hi-btn-${s.share_id}`}
                             target="_blank" rel="noopener">
                            🇮🇳 हिन्दी+EN
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Send-poster button — inline form that emails both PDFs to the nominee
// via the backend POST /locker/shares/{id}/email-poster endpoint. The
// email adapter is currently mocked (SendGrid to be wired) — but the
// endpoint still generates + attaches the PDFs and records an audit
// notification so the owner sees the attempt.
function SendPosterButton({ shareId, defaultTo }) {
  const [open, setOpen]     = useState(false);
  const [to, setTo]         = useState(defaultTo || "");
  const [note, setNote]     = useState("");
  const [busy, setBusy]     = useState(false);
  const send = async () => {
    if (!to || !to.includes("@")) { toast.error("Enter a valid email address."); return; }
    setBusy(true);
    try {
      await api.post(`/locker/shares/${shareId}/email-poster`, { to, note });
      toast.success(`Posters emailed to ${to}. Check the nominee's inbox in a minute.`);
      setOpen(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't send the email.";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <Button onClick={() => setOpen(true)}
              className="rounded-full bg-white/10 hover:bg-white/20 text-white text-[11px] font-black h-9"
              data-testid="share-email-poster-btn">
        📧 Email poster to nominee
      </Button>
    );
  }
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-3 space-y-2" data-testid="share-email-poster-form">
      <div className="text-[11px] font-black text-white">Send both posters to</div>
      <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="nominee@email.com"
             className="rounded-full bg-white/5 border-white/10 text-white text-[11px] h-9"
             data-testid="share-email-to"/>
      <Input value={note} onChange={(e) => setNote(e.target.value)}
             placeholder="Optional note for the nominee (e.g. 'Keep this on the fridge')"
             className="rounded-full bg-white/5 border-white/10 text-white text-[11px] h-9"
             data-testid="share-email-note"/>
      <div className="flex gap-2">
        <Button onClick={send} disabled={busy}
                className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[11px] h-8 flex-1"
                data-testid="share-email-send-btn">
          {busy ? <><Loader2 size={11} className="animate-spin mr-1"/>Sending…</> : "Send both PDFs"}
        </Button>
        <Button onClick={() => setOpen(false)}
                className="rounded-full bg-white/10 hover:bg-white/20 text-white font-black text-[11px] h-8"
                data-testid="share-email-cancel-btn">
          Cancel
        </Button>
      </div>
      <div className="text-[10px] text-white/40 leading-snug">
        Attaches emergency-access.pdf (EN) + emergency-access-hi-en.pdf (Hindi+English). Delivery uses the platform's email adapter — currently on mock; real send activates when production keys are configured.
      </div>
    </div>
  );
}

